<template>
    <div class="flex flex-col lg:flex-row lg:justify-between lg:items-end gap-2 lg:gap-3">
        <div class="w-full form-control">
            <label for="currentEmail" class="label">
                <span class="label-text">{{ $t('customer.editEmail.emailLabel') }}</span>
            </label>
            <div class="join w-full">
                <input
                    v-if="user != null"
                    id="currentEmail"
                    v-model="user.email"
                    disabled
                    type="text"
                    class="input border-none w-full join-item"
                >
                <div class="btn btn-square shadow-none cursor-not-allowed join-item">
                    <MiscIcon name="lock" class="w-5 h-5" />
                </div>
            </div>
        </div>
        <label
            ref="editEmailModalOpenBtn"
            tabindex="0"
            for="editEmailModal"
            class="link lg:w-48 lg:py-3"
            aria-haspopup="dialog"
            role="button"
            :aria-expanded="showEmailEditModal"
            @click.prevent="openModal()"
            @keydown.enter.prevent="openModal()"
        >
            {{ $t('customer.editEmail.editEmailAction') }}
        </label>
    </div>

    <input
        id="editEmailModal"
        v-model="showEmailEditModal"
        type="checkbox"
        class="modal-toggle"
    >
    <div v-if="showEmailEditModal" class="modal">
        <div class="modal-box relative w-11/12 max-w-4xl pt-4 px-6 pb-8">
            <div class="flex justify-between items-center pt-3 pb-5 border-b border-base-300 mb-6">
                <div class="text-base font-normal" role="heading" aria-level="2">
                    {{ $t('customer.editEmail.modalTitle') }}
                </div>
                <label
                    ref="editEmailModalCloseBtn"
                    tabindex="0"
                    for="editEmailModal"
                    role="button"
                    class="btn btn-ghost btn-circle"
                    :aria-label="$t('customer.editEmail.modalClose')"
                    @click.prevent="closeModal()"
                    @keydown.enter.prevent="closeModal()"
                >
                    <MiscIcon name="x" class="w-5 h-5" />
                    <span class="hidden">{{ $t('customer.editEmail.modalClose') }}</span>
                </label>
            </div>

            <form class="flex flex-col items-start gap-3" @submit.prevent="onSubmit()">
                <div class="form-control w-full">
                    <label for="editEmail" class="label">
                        <span class="label-text">{{ $t('customer.editEmail.emailLabel') }}</span>
                    </label>
                    <input
                        id="editEmail"
                        v-model="formData.email"
                        required
                        type="email"
                        inputmode="email"
                        :placeholder="$t('customer.editEmail.emailPlaceholder')"
                        class="input input-bordered w-full"
                    >
                </div>

                <div class="form-control w-full">
                    <label for="editEmailConfirmation" class="label">
                        <span class="label-text">{{ $t('customer.editEmail.emailConfirmationLabel') }}</span>
                    </label>
                    <input
                        id="editEmailConfirmation"
                        v-model="formData.emailConfirmation"
                        required
                        type="email"
                        inputmode="email"
                        :placeholder="$t('customer.editEmail.emailConfirmationPlaceholder')"
                        class="input input-bordered w-full"
                    >
                </div>

                <div class="form-control w-full">
                    <label for="register-password" class="label">
                        <span class="label-text">{{ $t('customer.editEmail.passwordLabel') }}</span>
                    </label>
                    <div class="join w-full">
                        <input
                            id="register-password"
                            v-model="formData.password"
                            required
                            :type="showPassword ? 'text' : 'password'"
                            :placeholder="$t('customer.editEmail.passwordPlaceholder')"
                            class="input input-bordered w-full border-r-0 join-item"
                        >
                        <div
                            class="btn btn-square btn-outline border-border border-l-0 join-item"
                            tabindex="0"
                            role="button"
                            :aria-pressed="showPassword"
                            :aria-label="showPassword ? $t('customer.passwordResetForm.hideInput') : $t('customer.passwordResetForm.showInput')"
                            @click.prevent="showPassword = !showPassword"
                            @keydown.enter.prevent="showPassword = !showPassword"
                        >
                            <MiscIcon v-if="!showPassword" name="eye" class="w-5 h-5" />
                            <MiscIcon v-else name="eye-off" class="w-5 h-5" />
                        </div>
                    </div>
                </div>

                <button
                    type="submit"
                    :disabled="loading"
                    :aria-disabled="loading"
                    class="btn btn-secondary mt-9"
                    data-cy="customer-edit-email-submit"
                >
                    <span v-if="loading" class="loading" />
                    {{ $t('customer.editEmail.modalSubmit') }}
                </button>
            </form>
        </div>
        <label
            tabindex="0"
            class="modal-backdrop cursor-pointer"
            for="editEmailModal"
        >
            {{ $t('customer.editEmail.modalClose') }}
        </label>
    </div>
</template>

<script setup lang="ts">
import { ApiClientError, type ApiError } from '@shopware/api-client'

const { t } = useI18n()
const { user, updateEmail, refreshUser } = useUser()

const showPassword = ref(false)
const loading = ref(false)
const formData = reactive({
    email: '',
    emailConfirmation: '',
    password: ''
})
const { showNotification } = useNotification()
const showEmailEditModal = ref(false)
const editEmailModalCloseBtn = ref<null | HTMLElement>(null)
const editEmailModalOpenBtn = ref<null | HTMLElement>(null)

async function onSubmit () {
    try {
        loading.value = true
        await updateEmail(formData)
        await refreshUser()
        showEmailEditModal.value = false
        formData.email = ''
        formData.emailConfirmation = ''
        formData.password = ''
        showNotification(t('customer.editEmail.formSuccess'), 'success')
    } catch (e: unknown) {
        if (e instanceof ApiClientError) {
            e.details?.errors.forEach((error: ApiError) => {
                if (error?.code === 'VIOLATION::CUSTOMER_VAT_ID_INVALID') {
                    showNotification(t('customer.editInfo.vatIdInvalid'), 'error')
                } else {
                    showNotification(error?.detail ?? t('customer.editInfo.error'), 'error')
                }
            })
        } else {
            showNotification(t('customer.editEmail.formError'), 'error', true)
        }
    } finally {
        loading.value = false
    }
}

watch (() => editEmailModalCloseBtn?.value, () => {
    editEmailModalCloseBtn?.value?.focus()
})

function closeModal () {
    showEmailEditModal.value = false
    editEmailModalOpenBtn.value?.focus()
}

function openModal () {
    showEmailEditModal.value = true
}
</script>
